// The topbar: explicit Task-title search and Ask Ritona controls on the left, and the Manager/Operator role
// switch on the right. Search reads the authorised Task-board projection; Ask opens the chat dock. Keeping
// those actions separate means a search query can never become a model prompt or silently reuse a Session.
//
// The role switch is a REAL enforced boundary in the owning read interfaces, NOT a UI affordance that hides
// controls (design brief §4, G-180). Here it only re-scopes which nav/surfaces render; the server authorises
// every read/route against the resolved Actor regardless of this toggle. Opening chat routes through the
// shell state (useShell) — the launcher is the compact-panel entry point (design brief §4).

import { useCallback, useEffect, useState } from 'react';
import type { DashboardRole } from './surfaces.ts';
import { Icon } from './Icon.tsx';
import { useShell } from './ShellContext.tsx';
import { TaskSearchPalette } from './TaskSearchPalette.tsx';

export interface TopbarProps {
  readonly role: DashboardRole;
  readonly onRoleChange: (role: DashboardRole) => void;
}

const ROLE_LABELS: ReadonlyArray<{ role: DashboardRole; label: string }> = [
  { role: 'admin_md', label: 'Manager' },
  { role: 'operator', label: 'Operator' },
];

/** The Manager/Operator segmented control (prototype .seg; aria-pressed marks the active role). */
function RoleSwitch({ role, onRoleChange }: TopbarProps): React.JSX.Element {
  return (
    <div role="group" aria-label="Role" className="seg">
      {ROLE_LABELS.map(({ role: candidate, label }) => {
        const isActive = candidate === role;
        return (
          <button
            key={candidate}
            type="button"
            aria-pressed={isActive}
            onClick={() => onRoleChange(candidate)}
            className={isActive ? 'on' : undefined}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

/** A separate Ask action: unlike Task search, this explicitly opens the conversational surface. */
function AskLauncher(): React.JSX.Element {
  const shell = useShell();
  return (
    <button
      type="button"
      onClick={shell.openChat}
      className="ask ask-ritona"
      aria-label="Open Ask Ritona"
    >
      <span className="ico">
        <Icon name="chat" size={16} />
      </span>
      Ask Ritona
    </button>
  );
}

/** Task-title search launcher + palette. ⌘K belongs to search and cannot trigger a chat send. */
function TaskSearchLauncher(): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
  return (
    <div className="task-search-wrap">
      <button
        type="button"
        className="ask task-search-launcher"
        aria-label="Search tasks"
        aria-expanded={open}
        aria-controls="task-search-palette"
        onClick={() => setOpen((current) => !current)}
      >
        <span className="ico">
          <Icon name="search" size={16} />
        </span>
        Search tasks
        <span className="kbd">⌘K</span>
      </button>
      <TaskSearchPalette open={open} onClose={close} />
    </div>
  );
}

/** The topbar row: the two explicit actions + a spacer + the role switch. */
export function Topbar({ role, onRoleChange }: TopbarProps): React.JSX.Element {
  return (
    <header className="topbar">
      <div className="topbar-actions">
        <TaskSearchLauncher />
        <AskLauncher />
      </div>
      <span className="spacer" />
      <RoleSwitch role={role} onRoleChange={onRoleChange} />
    </header>
  );
}
